import type { NoteType, WidgetConfig } from '@spotnote/shared/types'
import type { Point } from '@spotnote/shared/geometry'
import type { NoteTarget } from '@spotnote/shared/target'
import type { WidgetLocale } from './i18n'

/** A note the client has saved locally but not yet sent. */
export interface LocalNote {
  id: string
  n: number
  type: NoteType
  x?: number
  y?: number
  w?: number
  h?: number
  points?: Point[]
  cx: number
  cy: number
  comment: string
  scroll_y: number
  viewport_w: number
  viewport_h: number
  /** Elements under the annotation, most specific first. */
  targets: NoteTarget[]
}

/** An in-progress drag or stroke, before the composer opens. */
export type Draft =
  | { kind: 'rect'; x: number; y: number; x2: number; y2: number }
  | { kind: 'draw'; points: Point[] }

/** Where the composer is anchored, and what shape it will save. */
/**
 * Where the composer is anchored, what shape it will save, and which elements
 * sat underneath — resolved at draw time, because the page may have changed by
 * the time Save is pressed.
 */
export type ComposerTarget = { targets: NoteTarget[] } & (
  | { type: 'pin'; x: number; y: number; anchorX: number; anchorY: number }
  | { type: 'rect'; x: number; y: number; w: number; h: number; anchorX: number; anchorY: number }
  | { type: 'draw'; points: Point[]; anchorX: number; anchorY: number }
)

export interface WidgetState {
  config: WidgetConfig | null
  /**
   * The UI language. Lives in the store — not only in the i18n module — so
   * switching it re-renders everything currently on screen.
   */
  locale: WidgetLocale
  /** Feedback mode is on. */
  active: boolean
  tool: NoteType | null
  draft: Draft | null
  composer: ComposerTarget | null
  notes: LocalNote[]
  reviewing: boolean
  sending: boolean
  /** At least one session has been sent from this page load. */
  sent: boolean
  sentOverlay: boolean
  /** Notes in the session just sent — the live list is cleared on send. */
  sentCount: number
  error: string | null
}

export function initialState(): WidgetState {
  return {
    config: null,
    locale: 'en',
    active: false,
    tool: null,
    draft: null,
    composer: null,
    notes: [],
    reviewing: false,
    sending: false,
    sent: false,
    sentOverlay: false,
    sentCount: 0,
    error: null,
  }
}

type Listener = (state: WidgetState, previous: WidgetState) => void

export class Store {
  private state: WidgetState = initialState()
  private listeners: Listener[] = []

  get(): WidgetState {
    return this.state
  }

  set(patch: Partial<WidgetState> | ((s: WidgetState) => Partial<WidgetState>)): void {
    const previous = this.state
    const delta = typeof patch === 'function' ? patch(previous) : patch
    this.state = { ...previous, ...delta }
    for (const listener of this.listeners) listener(this.state, previous)
  }

  subscribe(listener: Listener): void {
    this.listeners.push(listener)
  }
}
